import { createFileRoute, Outlet, useLocation, useParams } from '@tanstack/react-router'
import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { DataTableSkeleton } from '@/components/route-shell/skeletons/DataTableSkeleton'
import { getContractsWithClausesByProject, CONTRACT_LIST_PAGE_SIZE } from '@/services/contractService'
import { contractWithClausesToContract, enrichContractRow } from './-vault/contractRow'
import { UPLOAD_BATCH_COMPLETED_EVENT, UPLOAD_CONTRACT_LOADED_EVENT } from '@/services/uploadBatchStore'
import type { FoldedChild } from '@/lib/contractFold'
import type { RelatedContractDto } from '@/services/foldedContracts'
import type { Contract, Clause } from '@/types'
import type { ContractWithTags } from './-vault-context'
import '@/styles/pages.css'
import { VaultContext } from './-vault-context'
import { useClientProject } from '@/contexts'
import { normalizeEntityId } from '@/lib/entityUrl'

interface VaultSearchParams {
  clientId?: string
  projectId?: string
  /** Deep-link target: open the contracts view scoped to this folder (used by the Data Center's
   *  Recent Uploads "Folder" links). Consumed once on arrival to seed the folder breadcrumb. */
  folderId?: string
}

export const Route = createFileRoute('/_app/law/vault')({
  head: () => ({ meta: [{ title: 'Vault · AERRIS' }] }),
  component: VaultLayout,
  validateSearch: (search: Record<string, unknown>): VaultSearchParams => ({
    clientId: typeof search.clientId === 'string' ? search.clientId : undefined,
    projectId: typeof search.projectId === 'string' ? search.projectId : undefined,
    folderId: typeof search.folderId === 'string' ? search.folderId : undefined,
  }),
})

function VaultLayout() {
  const location = useLocation()
  // The open contract (when the analysis child route is active) comes from its path param. That
  // route's params.parse decodes it to a UUID, but this non-strict read from the parent layout can
  // still see the raw base62 segment after an in-app navigation (the parent match's params are
  // snapshotted before the child's parse runs), so normalise it here too. It drives the contract
  // filter so returning to the table stays focused on the viewed contract.
  const { contractId: rawContractId } = useParams({ strict: false })
  const selectedContractId = normalizeEntityId(rawContractId) ?? rawContractId

  // Shared client/project state from context (persists across sections)
  const {
    clientId, projectId,
    clients, allProjects, projects,
    loadingClients, loadingProjects,
    selectedClient, selectedProject,
  } = useClientProject()

  // Vault-specific state
  const [allContracts, setAllContracts] = useState<Contract[]>([])
  const [allContractsWithTags, setAllContractsWithTags] = useState<ContractWithTags[]>([])
  // The server folds amendment/superseding children into their roots, so this mirrors `allContracts`
  // (one row per folded root, each carrying its own parties). Kept as a separate list for the
  // party-centric views that read it; folded-away children are no longer returned as their own rows.
  const [allRawContracts, setAllRawContracts] = useState<Contract[]>([])
  const [loadingContracts, setLoadingContracts] = useState(true)
  const [loadingClauses, setLoadingClauses] = useState(true)
  // Set when contract fetches failed and nothing loaded, so views can show a
  // real error (with retry) instead of a misleading "no contracts" empty state.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadCounter, setReloadCounter] = useState(0)
  const [contractFilter, setContractFilter] = useState('')
  const [clausesByContract, setClausesByContract] = useState<Record<string, Clause[]>>({})

  // Contracts deleted while the progressive loader is still paging. The loader's
  // accumulators outlive removeContract's state filter, so each page flush must
  // re-exclude these or a deleted contract reappears in the table.
  const deletedContractUuidsRef = useRef<Set<string>>(new Set())

  // The client:project whose contracts completed a load. When a reload fires for the SAME key
  // (ingest ticks bumping reloadCounter, or a projects-list identity change), it runs as a SILENT
  // background refresh: no wipe, no loading flag, one commit at the end — the table stays mounted
  // and interactive (an open sort dropdown survives). Wiping + flipping loadingContracts swapped
  // the whole table for a spinner on every ingested contract, killing any in-progress interaction.
  const loadedKeyRef = useRef<string | null>(null)

  // Sync contractFilter with the contract open in the analysis child route
  useEffect(() => {
    if (!selectedContractId) {
      setContractFilter('')
      return
    }

    const selectedContract = allContracts.find((contract) => contract.uuid === selectedContractId)
    setContractFilter(selectedContract?.nameOfAgreement || selectedContract?.name || '')
  }, [selectedContractId, allContracts])

  // Clear contract filter when navigating away from specific contract view
  const isOnAnalysisPage = location.pathname.includes('/analysis/')
  useEffect(() => {
    if (!isOnAnalysisPage) {
      setContractFilter('')
    }
  }, [isOnAnalysisPage])

  // Lazily load contracts for the SELECTED project only, paging progressively.
  // The client / all-projects list views (ClientsView, ProjectsView) use
  // server-provided counts and never render a contract table, so they need no
  // bodies; and Clause Relationships never cross a Project (OukaiServer ADR 0001),
  // so per-project scope is sufficient for every consumer (table,
  // relationship graph). This replaces the old eager load of every project's
  // contracts up front.
  useEffect(() => {
    if (!projectId) {
      setAllContracts([])
      setAllContractsWithTags([])
      setClausesByContract({})
      setLoadingContracts(false)
      setLoadingClauses(false)
      setLoadError(null)
      loadedKeyRef.current = null // lists are gone — the next selection is a full (visible) load
      return
    }

    // Only fold the projects the current view actually shows. getContractsWithClausesByProject now
    // hits /data/read (server-side fold) per project, so paging over EVERY project on landing fires
    // a fold request for the whole system. Scope it: the selected project, else the selected client's
    // projects, else none — the top-level clients view reads counts from the client objects, not folds.
    const projectsToLoad = projectId
      ? allProjects.filter((p) => p.uuid === projectId)
      : clientId
        ? allProjects.filter((p) => p.clientUuid === clientId)
        : []

    const abortController = new AbortController()
    const aborted = () => abortController.signal.aborted

    // Same client+project already on screen → background refresh (see loadedKeyRef).
    const loadKey = `${clientId ?? ''}:${projectId ?? ''}`
    const isRefresh = loadedKeyRef.current === loadKey

    if (!isRefresh) {
      setLoadingContracts(true)
      setLoadingClauses(true)
      setLoadError(null)
      setAllContracts([])
      setAllContractsWithTags([])
      setClausesByContract({})
      deletedContractUuidsRef.current = new Set()
    }

    async function loadContractsAndClauses() {
      let offset = 0
      let hasMore = true
      let fetchFailures = 0
      // Projects whose server response still reports more roots at the current offset. Starts as all
      // of them; a project drops out once its page's hasMore is false, so later pages (only reached
      // when a project exceeds CONTRACT_LIST_PAGE_SIZE roots) don't re-fold finished projects.
      let activeProjects = projectsToLoad

      // Raw, per-contract data for EVERY contract in the project (originals and
      // amendment children alike). Consolidation runs over the full accumulated set
      // on each flush, so a child whose parent arrives on a later page still folds
      // correctly. clausesByContract is kept un-consolidated and keyed by every uuid,
      // so deep-links into a hidden child contract still resolve its own clauses.
      const accContracts: Contract[] = []
      const accClauses: Record<string, Clause[]> = {}
      // The server's fold maps (root uuid → folded amendment children / folded supplements), accumulated
      // across pages and projects. The client fold can't re-derive these (it only sees already-folded
      // roots), so the "+N amendments" / "+N supplements" name badges read from here.
      const accChildrenByRoot: Record<string, FoldedChild[]> = {}
      const accSupplementsByRoot: Record<string, FoldedChild[]> = {}
      // Root uuid → every document folded under it, tagged by kind. The "superseded"/"cancelled"
      // entries are the whole-document SUPERSEDES/CANCELS predecessors — folded out of the list, so
      // (like the two maps above) they can only be surfaced from here, never re-derived client-side.
      const accRelatedByRoot: Record<string, RelatedContractDto[]> = {}

      while (hasMore) {
        if (aborted()) return

        const pages = await Promise.all(
          activeProjects.map((project) =>
            getContractsWithClausesByProject(project.uuid, offset, CONTRACT_LIST_PAGE_SIZE)
              .catch((err) => {
                console.warn(`Failed to fetch contracts for project ${project.uuid}:`, err)
                fetchFailures += 1
                // Treat a failed page as terminal for this project (hasMore:false) — matches the prior
                // behaviour of not retrying a project that returned a short page.
                return { contracts: [], childrenByRoot: {}, supplementsByRoot: {}, relatedByRoot: {}, hasMore: false, nextOffset: null }
              })
              .then((page) => ({ project, page }))
          )
        )

        if (aborted()) return

        for (const { page } of pages) {
          Object.assign(accChildrenByRoot, page.childrenByRoot)
          Object.assign(accSupplementsByRoot, page.supplementsByRoot)
          Object.assign(accRelatedByRoot, page.relatedByRoot)
          for (const c of page.contracts) {
            accContracts.push(contractWithClausesToContract(c))
            accClauses[c.uuid] = c.clauses as Clause[]
          }
        }

        // The server already folded amendment children AND supplements into their roots per project before
        // paginating, so the contracts that arrive ARE the folded roots — no client re-fold is needed.
        // (Folding here would also risk crossing project boundaries, which the per-project server fold
        // avoids.) The "+N amendments" / "+N supplements" affordances (foldedChildren / foldedSupplements)
        // still come from the server's childrenByRoot / supplementsByRoot maps.
        const commit = () => {
          const deleted = deletedContractUuidsRef.current
          const rootContracts = accContracts.filter((c) => !deleted.has(c.uuid))

          const withTags: ContractWithTags[] = rootContracts.map((root) =>
            enrichContractRow(root, {
              childrenByRoot: accChildrenByRoot,
              supplementsByRoot: accSupplementsByRoot,
              relatedByRoot: accRelatedByRoot,
            })
          )

          setAllContracts(rootContracts)
          setAllContractsWithTags(withTags)
          setAllRawContracts(rootContracts)
          setClausesByContract(Object.fromEntries(
            Object.entries(accClauses).filter(([uuid]) => !deleted.has(uuid))
          ))
          setLoadingContracts(false)
        }

        // Initial/context-switch load: flush every page so the table paints progressively.
        // Background refresh: stay silent until ALL pages are in (a per-page flush would shrink
        // the on-screen list to the accumulated prefix and regrow it — visible churn). "All in"
        // uses the server's own pagination signal: no project reports more roots remaining.
        if (!isRefresh) commit()
        else if (!pages.some(({ page }) => page.hasMore)) commit()

        // Trust the server's fold pagination instead of guessing from page length: keep paging only
        // the projects whose response says roots remain (exact — no wasted trailing "confirming"
        // request, and correct when a project's root count is an exact multiple of the page size).
        activeProjects = pages.filter(({ page }) => page.hasMore).map(({ project }) => project)
        hasMore = activeProjects.length > 0
        offset += CONTRACT_LIST_PAGE_SIZE
      }

      if (!aborted()) {
        setLoadingClauses(false)
        // Every byte of contract data failed to arrive — surface it as an error, not an empty
        // project. On a refresh, the stale-but-real list stays on screen (nothing was committed).
        if (accContracts.length === 0 && fetchFailures > 0) {
          setLoadError('Contracts could not be loaded. The server may be unreachable.')
          return
        }
        loadedKeyRef.current = loadKey
      }
    }

    loadContractsAndClauses().catch((err) => {
      console.error('Failed to load contracts:', err)
      if (!aborted()) {
        setLoadingContracts(false)
        setLoadingClauses(false)
        setLoadError('Contracts could not be loaded. The server may be unreachable.')
      }
    })

    return () => {
      abortController.abort()
    }
  }, [loadingProjects, allProjects, reloadCounter, projectId, clientId])


  // Memoized derived data to prevent recalculation on every render
  const contracts = useMemo(() => {
    if (projectId) return allContracts.filter((c) => c.projectUuid === projectId)
    if (clientId) {
      return allContracts.filter((c) => {
        const project = allProjects.find((p) => p.uuid === c.projectUuid)
        return project?.clientUuid === clientId
      })
    }
    return allContracts
  }, [projectId, clientId, allContracts, allProjects])

  // Same project/client scoping as `contracts`, but over the unconsolidated list (folded children included).
  const rawContracts = useMemo(() => {
    if (projectId) return allRawContracts.filter((c) => c.projectUuid === projectId)
    if (clientId) {
      return allRawContracts.filter((c) => {
        const project = allProjects.find((p) => p.uuid === c.projectUuid)
        return project?.clientUuid === clientId
      })
    }
    return allRawContracts
  }, [projectId, clientId, allRawContracts, allProjects])

  const contractsWithTags = useMemo(() => {
    if (projectId) return allContractsWithTags.filter((c) => c.projectUuid === projectId)
    if (clientId) {
      return allContractsWithTags.filter((c) => {
        const project = allProjects.find((p) => p.uuid === c.projectUuid)
        return project?.clientUuid === clientId
      })
    }
    return allContractsWithTags
  }, [projectId, clientId, allContractsWithTags, allProjects])

  const filteredContracts = useMemo(() => {
    let filtered = contractsWithTags

    // Filter by contract name
    if (contractFilter) {
      const filterLower = contractFilter.toLowerCase()
      filtered = filtered.filter((c) =>
        (c.name ?? '').toLowerCase().includes(filterLower) ||
        (c.nameOfAgreement ?? '').toLowerCase().includes(filterLower)
      )
    }

    return filtered
  }, [contractsWithTags, contractFilter])

  // Re-fetch the selected project's contracts. For an already-loaded project this runs
  // as a silent background refresh (see loadedKeyRef), so callers use it both to retry
  // a failed load and to re-sync the list with the server after a delete.
  const retryLoadContracts = useCallback(() => {
    setReloadCounter((n) => n + 1)
  }, [])

  // Refresh the vault list when an upload batch finishes — its new contracts are now in the graph.
  useEffect(() => {
    const onCompleted = () => setReloadCounter((n) => n + 1)
    window.addEventListener(UPLOAD_BATCH_COMPLETED_EVENT, onCompleted)
    return () => window.removeEventListener(UPLOAD_BATCH_COMPLETED_EVENT, onCompleted)
  }, [])

  // Also refresh as each contract finishes loading, so new contracts appear incrementally
  // instead of only at batch end. Debounced so a burst of completions triggers one refetch.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const onContractLoaded = () => {
      clearTimeout(timer)
      timer = setTimeout(() => setReloadCounter((n) => n + 1), 600)
    }
    window.addEventListener(UPLOAD_CONTRACT_LOADED_EVENT, onContractLoaded)
    return () => {
      clearTimeout(timer)
      window.removeEventListener(UPLOAD_CONTRACT_LOADED_EVENT, onContractLoaded)
    }
  }, [])

  const removeContract = useCallback((uuid: string) => {
    deletedContractUuidsRef.current.add(uuid)
    setAllContracts((prev) => prev.filter((c) => c.uuid !== uuid))
    setAllContractsWithTags((prev) => prev.filter((c) => c.uuid !== uuid))
    setAllRawContracts((prev) => prev.filter((c) => c.uuid !== uuid))
    setClausesByContract((prev) => {
      if (!(uuid in prev)) return prev
      const next = { ...prev }
      delete next[uuid]
      return next
    })
  }, [])

  // Memoize context value to prevent unnecessary rerenders of consumers
  // IMPORTANT: This must be BEFORE any early returns to comply with Rules of Hooks
  const contextValue = useMemo(() => ({
    clientId,
    projectId,
    clients,
    projects,
    contracts,
    contractsWithTags,
    rawContracts,
    allProjects,
    allContracts,
    allContractsWithTags,
    filteredContracts,
    selectedClient,
    selectedProject,
    loadingClients,
    loadingProjects,
    loadingContracts,
    loadingClauses,
    contractFilter,
    setContractFilter,
    clausesByContract,
    removeContract,
    loadError,
    retryLoadContracts,
  }), [
    clientId, projectId, clients, projects, contracts, contractsWithTags, rawContracts,
    allProjects, allContracts, allContractsWithTags, filteredContracts,
    selectedClient, selectedProject,
    loadingClients, loadingProjects, loadingContracts, loadingClauses,
    contractFilter, clausesByContract, removeContract,
    loadError, retryLoadContracts
  ])

  if (loadingClients) {
    // The vault is a table destination: hold its shape while the client list arrives
    // (ADR 0027 — skeletons over spinners for first loads).
    return (
      <div className="page-container">
        <DataTableSkeleton />
      </div>
    )
  }

  return (
    <div className="page-container" style={{ overflow: 'hidden' }}>
      {/* Main content / Search results row — this div scrolls independently */}
      <VaultContext.Provider value={contextValue}>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Outlet />
        </div>
      </VaultContext.Provider>
    </div>
  )
}
